import { chatHistorySampleData } from '../constants/chatHistory'
import { isTeamsSession, withTeamsSessionRetry } from '../utils/teamsAuth'
import { config } from '#/config'
import { trackApiFailure } from '../utils/telemetry'
import {
  BotConversations,
  BotResponse,
  ClientSources,
  Conversation,
  ConversationRequest,
  CosmosDBHealth,
  CosmosDBStatus,
  UserInfo
} from './models'

// ---------------------------------------------------------------------------
// Shared auth telemetry helpers (zero performance overhead — fire-and-forget)
// ---------------------------------------------------------------------------

/** Returns the correlation ID injected by the server at page load. */
const _correlationId = (): string =>
  typeof window !== 'undefined' && config.env.CORRELATION_ID ? config.env.CORRELATION_ID : ''

/**
 * Call after any fetch response.  Logs to console + App Insights on 401/403.
 * Returns the original response unchanged so existing callers are unaffected.
 */
const _warnAuthFailure = (res: Response, endpoint: string): Response => {
  if (res.status === 401 || res.status === 403) {
    const tokenExpiry = localStorage.getItem('tokenExpiry') ?? ''
    const userId = localStorage.getItem('userId') ?? ''
    trackApiFailure(endpoint, res.status, {
      user_id: userId,
      token_expiry: tokenExpiry,
      correlation_id: _correlationId()
    })
  }
  return res
}

export async function conversationApi(options: ConversationRequest, abortSignal: AbortSignal): Promise<Response> {
  const response = await fetch('/api/conversation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages: options.messages
    }),
    signal: abortSignal
  })

  return response
}

export async function getUserInfo(accessToken: string): Promise<UserInfo[]> {
  const response = await fetch('/api/.auth/me', {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  if (!response.ok) {
    console.log('No identity provider found. Access to chat will be blocked.')
    return []
  }

  const payload = await response.json()
  console.log(payload)
  return payload
}

// export const fetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
export const fetchChatHistoryInit = (): Conversation[] | null => {
  // Make initial API call here

  return chatHistorySampleData
}

export const historyList = async (
  offset = 0,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<BotConversations[] | null> => {
  const response = await fetch(`/api/history/list?offset=${offset}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(async res => {
      _warnAuthFailure(res, '/history/list')
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }

      const conversations: BotConversations[] = payload.filter(conv => conv.dataSource?.toLowerCase() !== 'vayu').map((conv: any) => ({
        id: conv.id,
        title: conv.title,
        date: conv.createdAt,
        messages: [], // ✅ Empty initially - will be loaded on click
        token: conv.token,
        bconvid: conv.bconvId,
        dataSource: conv.dataSource
      }))
      return conversations
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const historyRead = async (
  convId: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<BotResponse[]> => {
  const response = await fetch('/api/history/read', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(async res => {
      _warnAuthFailure(res, '/history/read')
      const payload = await res.json()
      const messages: BotResponse[] = []
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: BotResponse = {
            id: msg.id,
            role: msg.role,
            date: msg.createdAt,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
            type: msg.type,
            timestamp: msg.createdAt,
            channelId: '',
            from: {
              id: msg.userId,
              name: ''
            },
            conversation: {
              id: msg.botConversationId,
              title: '',
              messages: [],
              date: ''
            },
            text: '',
            inputHint: '',
            context: '',
            messageType: msg.messageType,
            card: msg.card,
            token: msg.token,
            streamUrl: msg.streamUrl,
            orderId: undefined
          }

          if (message.role === 'tool') {
            try {
              let citation = JSON.parse(msg.content)

              if (citation.citations) {
                message.content = JSON.stringify(citation.citations)
              }
            } catch (err) {}
          }

          messages.push(message)
        })
      }
      return messages
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return []
    })
  return response
}

export const historyUpdate = async (
  messages: BotResponse[],
  convId: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response> => {
  const response = await fetch('/api/history/update', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(async res => {
      _warnAuthFailure(res, '/history/update')
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDelete = async (
  convId: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response> => {
  const response = await fetch('/api/history/delete', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response> => {
  const response = await fetch('/api/history/delete_all', {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyClear = async (
  convId: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response> => {
  const response = await fetch('/api/history/clear', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyRename = async (
  convId: string,
  title: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response> => {
  const response = await fetch('/api/history/rename', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyEnsure = async (
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<CosmosDBHealth> => {
  const response = await fetch('/api/history/ensure', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(async res => {
      _warnAuthFailure(res, '/history/ensure')
      const respJson = await res.json()
      console.log('Response from /history/ensure:', respJson)
      let formattedResponse
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        return {
          cosmosDB: false,
          status: formattedResponse
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.', err)
      return {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured
      }
    })
  return response
}

export const frontendSettings = async (): Promise<Response | null> => {
  const response = await fetch('/api/frontend_settings', {
    method: 'GET'
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}
export const historyMessageFeedback = async (
  messageId: string,
  feedback: string,
  emailClaim: string,
  userClaim: string,
  accessToken: string,
  conversationId: string,
  orderId?: number
): Promise<Response> => {
  const response = await fetch('/api/history/message_feedback', {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback,
      order_id: orderId,
      conversation_id: conversationId
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(res => {
      _warnAuthFailure(res, '/history/message_feedback')
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}
export const blobSAStoken = async (
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<Response | null> => {
  const response = await fetch('/api/env/getkey', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('Invalid SAS Token')
      return null
    })

  return response
}
export const botName = async (): Promise<string> => {
  const response = await fetch('/api/env/getBotName', {
    method: 'GET'
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('Invalid Bot Name')
      return null
    })

  return response
}
export const directLineSecretKey = async (
  emailClaim: string,
  userClaim: string,
  accessToken: string
): Promise<string> => {
  const response = await fetch('/api/env/getDirectLineSecretKey', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Email': emailClaim,
      'X-User-Id': userClaim
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('Invalid Secret Key')
      return null
    })

  return response
}

export const getDirectLineToken = async (emailClaim: string, userClaim: string, accessToken: string): Promise<any> => {
  console.log('X-User-Id : dl_' + userClaim)

  try {
    const response = await fetch('/api/env/getToken', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userClaim,
        email_id: emailClaim,
        access_token: accessToken
      }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Email': emailClaim,
        'X-User-Id': userClaim,
        'X-Correlation-Id': _correlationId()
      }
    })

    if (!response.ok) {
      _warnAuthFailure(response, '/env/getToken')
      throw new Error(`Failed to fetch token, status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (err) {
    console.error('Error fetching direct line token:', err)
    return null
  }
}

export const refreshToken = async (currentToken: string): Promise<any> => {
  const response = await fetch('/api/env/refreshToken', {
    method: 'POST',
    body: JSON.stringify({
      token: currentToken
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('Invalid Secret Key')
      return null
    })

  return response
}

export const signIn = async (currentToken: string, directLineToken: string, conversationId: string): Promise<any> => {
  const response = await fetch('/api/directline/signin', {
    method: 'POST',
    body: JSON.stringify({
      token: currentToken,
      directline_token: directLineToken,
      conversation_id: conversationId
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('unable to sign in')
      return _err
    })

  return response
}

// New API Implementation

const API_URL = config.env.API_URL

export const getListAvailableAgents = async (accessToken: string): Promise<any | null> => {
  return await fetch(`${API_URL}/list_available_agents`, {
    //changes for SSL certificate error fix
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-Correlation-Id': _correlationId()
    }
  })
    .then(res => {
      _warnAuthFailure(res, `${API_URL}/list_available_agents`)
      if (!res.ok) {
        throw new Error('Failed to fetch: ' + res.statusText)
      }
      return res.json()
    })
    .catch(err => {
      console.error('Error fetching available agents:', err)
      return null
    })
}

export const createNewConversation = async (agent: string, accessToken: string): Promise<any> => {
  const response = await fetch('/api/new_conversation', {
    method: 'POST',
    body: JSON.stringify({
      conversation_agent: agent
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('Error in createNewConversation')
      return _err
    })

  return response
}

export const postFunction = async (conversationId: string, accessToken: string): Promise<any> => {
  const response = await fetch('/api/post_function', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('unable to sign in')
      return _err
    })

  return response
}

export const postMessage = async (conversationId: string, accessToken: string, userMessage: string): Promise<any> => {
  const response = await fetch('/api/post_messages', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId,
      user_message: userMessage
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('unable to post_messages')
      return _err
    })

  return response
}

// export const getListAvailableAgents = (authHeader?: string): Promise<any> => {
//   return new Promise((resolve, reject) => {
//     if (!authHeader) {
//       reject(new Error("Authorization header is missing or invalid."));
//       return;
//     }

//     fetch(`${API_URL}/list_available_agents`, {
//       method: "GET",
//       headers: {
//         Authorization: `Bearer ${authHeader}`,
//       },
//     })
//       .then((response) => {
//         if (!response.ok) {
//           return response.text().then((text) => {
//             throw new Error(`Error fetching available agents: ${text}`);
//           });
//         }
//         return response.json();
//       })
//       .then((data) => {
//         resolve(data);
//       })
//       .catch((error) => {
//         // Replace this with proper logging in production
//         console.error("Error fetching available agents:", error);
//         reject(error);
//       });
//   });
// };

//  creating a new conversation
export const createNewConversationNew = async (
  agent: string,
  accessToken: string,
  clientSource: ClientSources = ClientSources.Browser
): Promise<any> => {
  try {
    console.log('API URL:', `${API_URL}/new_conversation`)
    console.log('Authorization Header:', accessToken ? `Bearer ${accessToken}` : 'No Authorization Header')

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Client-Source': clientSource
    }

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
      headers['X-Correlation-Id'] = _correlationId()
    }

    const doFetch = (): Promise<Response> =>
      fetch(`${API_URL}/new_conversation`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ agent })
      })

    // Teams session: same call, wrapped for automatic session-refresh-and-retry on 401
    const response = isTeamsSession() ? await withTeamsSessionRetry(doFetch) : await doFetch()

    if (!response.ok) {
      _warnAuthFailure(response, `${API_URL}/new_conversation`)
      const errorDetails = await response.text()
      console.error(`Response Status: ${response.status}, StatusText: ${response.statusText}, Details: ${errorDetails}`)
      throw new Error(
        `Error creating conversation: ${response.statusText} - ${errorDetails} - responseStatus: ${response.status}`
      )
    }

    return await response.json()
  } catch (error) {
    console.error('Error creating conversation:', error)
    throw new Error(`Failed to create conversation: ${error instanceof Error ? error.message : String(error)}`)
  }
}

//  posting a function
// export const postFunction = async (
//   conversationId: string,
//   data: PostFunctionRequest,
//   authHeader?: string
// ): Promise<void> => {
//   try {
//       const response = await fetch(`${API_URL}/${conversationId}/post_function`, {
//           method: 'POST',
//           headers: {
//               Authorization: `Bearer ${authHeader}`,
//               'Content-Type': 'application/json',
//           },
//           body: JSON.stringify(data),
//       });

//       if (!response.ok) {
//           throw new Error(`Error posting function: ${response.statusText}`);
//       }

//       // No need to process the response body here, as the function has no response data
//   } catch (error) {
//       console.error("Error posting function:", error);
//       throw error;
//   }
// };

//  posting a message
// export const postMessage = async (
//   conversationId: string,
//   data: PostMessageRequest,
//   authHeader?: string
// ): Promise<void> => {
//   try {
//       const response = await fetch(`${API_URL}/${conversationId}/messages`, {
//           method: 'POST',
//           headers: {
//               Authorization: `Bearer ${authHeader}`,
//               'Content-Type': 'application/json',
//           },
//           body: JSON.stringify(data),
//       });

//       if (!response.ok) {
//           throw new Error(`Error posting message: ${response.statusText}`);
//       }

//       // No need to process the response body here either
//   } catch (error) {
//       console.error("Error posting message:", error);
//       throw error;
//   }
// };
